/**
 * The practice loop (brief §practice: `/practice/session`). State lives in
 * memory and `sessionStorage` so a refresh survives; keyboard shortcuts fire
 * only when focus is outside a text input (`docs/CONVENTIONS.md`).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useLocalizedText } from "@client/components/Localized";
import { ArrowRight } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Navigate } from "react-router";
import { parseNumericAnswer } from "@shared/grading";
import type { ConfidenceLevel } from "@shared/attemptAnswer";
import { ApiError } from "@client/api/client";
import { Button, Kbd, ProgressBar } from "@client/components/ui";
import { useToast } from "@client/components/ui";
import { useT } from "@client/i18n";
import { isCode, messageForError } from "@client/lib/errors";
import { shortcutsBlocked } from "@client/lib/keyboard";
import { useFocusableOverflow } from "@client/lib/useFocusableOverflow";
import { LG_MEDIA_QUERY, useMediaQuery } from "@client/lib/useMediaQuery";
import {
  practiceKeys,
  useAnswerQuestion,
  useCheckPart,
  useHint,
  useRelatedQuestions,
  useRevealQuestion,
} from "./api";
import { AnswerArea } from "./AnswerArea";
import type { ConfidenceControlHandle } from "./ConfidenceControl";
import { patchStoredResult, sendLeaveAnswer } from "./leaveSubmit";
import { FeedbackPanel } from "./FeedbackPanel";
import { FollowUpBanner } from "./FollowUpBanner";
import { contrastOfferViews, followUpBody, insertContrast, similarState } from "./followUp";
import { FormulaPanel } from "./FormulaPanel";
import { HintLadder } from "./HintLadder";
import { NextSteps } from "./NextSteps";
import { PlanGate, PlanStep, type PlanStepHandle } from "./PlanStep";
import { QuestionCard } from "./QuestionCard";
import {
  followUpPatch,
  isPending,
  keptPartIds,
  pendingPatch,
  retriedPatch,
  retryBody,
  skippedPatch,
  terminalPatch,
  type SentTry,
} from "./retryFlow";
import { RetryPanel } from "./RetryPanel";
import type { SegmentedHandle } from "./SegmentedNumeric";
import { ResultSelfExplanation, SelfExplanation } from "./SelfExplanation";
import {
  activePart,
  isSegmentable,
  loadShowAllParts,
  saveShowAllParts,
  segmentedBody,
  type SegmentedDraft,
} from "./segmented";
import {
  capOpenText,
  clearSession,
  insertAfter,
  loadSession,
  openBelowFullMarks,
  saveSession,
  SESSION_PATCHED_EVENT,
  composeMissed,
  type MissedChip,
  type PartCheck,
  type PracticeSessionData,
  type QuestionResult,
  type SessionPatchedDetail,
} from "./session";
import { SessionSummary } from "./SessionSummary";
import type { AnswerBody, AnswerResponse, ContrastOffer } from "./types";
import { TimedHeader } from "./sets/TimedHeader";

export function PracticeSessionPage() {
  const t = useT();
  const localize = useLocalizedText();
  const toast = useToast();
  const [session, setSession] = useState<PracticeSessionData | null>(() => loadSession());

  // Draft answers are keyed by question id (not held in one flat "current
  // answer" slot) so that selecting an option lands in the *new* question's
  // slot no matter how the click and the question-advance render interleave.
  // A flat slot reset by `useEffect(() => setMcSelected([]), [current?.id])`
  // raced: the effect fires asynchronously after the render that advances
  // `current`, so a click in that window set the selection and the
  // still-pending reset then clobbered it, leaving "Svara" disabled under an
  // option that visibly showed checked.
  const [mcSelectedByQuestion, setMcSelectedByQuestion] = useState<Record<string, string[]>>({});
  const [numericValuesByQuestion, setNumericValuesByQuestion] = useState<
    Record<string, Record<string, string>>
  >({});
  const [openTextByQuestion, setOpenTextByQuestion] = useState<Record<string, string>>({});
  const [openTickedByQuestion, setOpenTickedByQuestion] = useState<Record<string, string[]>>({});
  const [openCorrectionByQuestion, setOpenCorrectionByQuestion] = useState<Record<string, string>>(
    {},
  );
  const [openMissedByQuestion, setOpenMissedByQuestion] = useState<Record<string, string>>({});
  // P1-9: the quick answer to "what did you miss", per question.
  const [openMissedChipByQuestion, setOpenMissedChipByQuestion] = useState<
    Record<string, MissedChip>
  >({});
  // True when a save was refused because "what did you miss" was empty below full marks.
  const [openMissedErrorByQuestion, setOpenMissedErrorByQuestion] = useState<
    Record<string, boolean>
  >({});
  const [justificationByQuestion, setJustificationByQuestion] = useState<Record<string, string>>(
    {},
  );
  // True when a flagged MC answer was refused because the justification was empty.
  const [justificationErrorByQuestion, setJustificationErrorByQuestion] = useState<
    Record<string, boolean>
  >({});
  // True when a submit was refused because no confidence rating was picked (§13 Q1).
  const [confidenceErrorByQuestion, setConfidenceErrorByQuestion] = useState<
    Record<string, boolean>
  >({});
  // True when a part check was refused because the value was empty or not a number (B4).
  const [partValueErrorByQuestion, setPartValueErrorByQuestion] = useState<Record<string, boolean>>(
    {},
  );
  // Per-viewer switch to today's all-parts form (`mf.practice.showAllParts`, pedagogy spec §4.3.4).
  const [showAllParts, setShowAllParts] = useState(() => loadShowAllParts());
  // False once the page is gone. A request that answers after that must not start another one or
  // write to component state (it is dropped); `updateResult` folds it into the stored session.
  const mountedRef = useRef(true);
  const confidenceRef = useRef<ConfidenceControlHandle>(null);
  const planRef = useRef<PlanStepHandle>(null);
  const segmentedRef = useRef<SegmentedHandle>(null);

  const answerMutation = useAnswerQuestion();
  const revealMutation = useRevealQuestion();
  const hintMutation = useHint();
  const relatedMutation = useRelatedQuestions();
  const checkPartMutation = useCheckPart();
  const queryClient = useQueryClient();

  const current =
    session && session.index < session.questions.length
      ? session.questions[session.index]
      : undefined;
  const currentResult = current && session ? session.results[current.id] : undefined;

  // UX-03: "Nästa" unmounts the terminal question's whole panel, and the new
  // question's controls start unanswered — without an explicit move, focus
  // falls to `<body>`. Only on an actual question change, never on the
  // page's first mount (there is nothing to "arrive from" yet). The summary is
  // `current === undefined`, so "Öva på liknande uppgifter" (summary unmounts,
  // a fresh session's first question mounts) counts as a change as well.
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const currentQuestionId = current?.id;
  const prevQuestionId = useRef<string | undefined>(currentQuestionId);
  useEffect(() => {
    if (currentQuestionId !== undefined && prevQuestionId.current !== currentQuestionId) {
      h1Ref.current?.focus();
    }
    prevQuestionId.current = currentQuestionId;
  }, [currentQuestionId]);

  // Two columns from `lg` (B3): the statement column sticks and scrolls on its own, so it
  // must be a keyboard tab stop when it overflows (axe `scrollable-region-focusable`). Below
  // `lg` it is plain flow content and is not scanned.
  const problemRef = useRef<HTMLDivElement>(null);
  const problemHeadingId = useId();
  const workHeadingId = useId();
  const twoColumns = useMediaQuery(LG_MEDIA_QUERY);
  useFocusableOverflow(problemRef, currentQuestionId, {
    enabled: twoColumns,
    label: t("common.scrollableContent"),
  });

  const mcSelected = current ? (mcSelectedByQuestion[current.id] ?? []) : [];
  const numericValues = current ? (numericValuesByQuestion[current.id] ?? {}) : {};
  const openText = current ? (openTextByQuestion[current.id] ?? "") : "";
  const openTicked = current ? (openTickedByQuestion[current.id] ?? []) : [];
  const openCorrection = current ? (openCorrectionByQuestion[current.id] ?? "") : "";
  const openMissed = current ? (openMissedByQuestion[current.id] ?? "") : "";
  const openMissedChip = current ? openMissedChipByQuestion[current.id] : undefined;
  const openMissedError = current ? (openMissedErrorByQuestion[current.id] ?? false) : false;
  // A reload keeps the stored session, not the drafts: while the retry is pending the first try's
  // justification comes back as the starting text, or "Kontrollera igen" would refuse to send.
  const justification = current
    ? (justificationByQuestion[current.id] ??
      (currentResult?.retryPending === true ? currentResult.justification : undefined) ??
      "")
    : "";
  const justificationError = current ? (justificationErrorByQuestion[current.id] ?? false) : false;
  const hintsUsed = currentResult?.hintsRevealed.length ?? 0;
  const confidenceError = current ? (confidenceErrorByQuestion[current.id] ?? false) : false;
  // Plan first (pedagogy spec §4.3.3): only questions of a mixed batch with an offer.
  const planOffer = current ? session?.plans?.[current.id] : undefined;
  const planLocked = planOffer !== undefined && currentResult?.planPick === undefined;
  const planBody =
    currentResult?.planPick !== undefined ? { plan: { topicId: currentResult.planPick } } : {};
  // Today's hand-off (step 2.7b): a first try carries where the item came from.
  const origin = current ? session?.origins?.[current.id] : undefined;
  const originBody = origin ? { origin } : {};
  // An inserted follow-up (step 2.6): the first try names the question it follows and, for a
  // contrast twin, the prediction made before it was inserted. A retry sends neither.
  const followUpFields = followUpBody(currentResult);
  // The contrast twins this question's terminal answer offered, and how many were inserted from it.
  const contrastViews =
    session && current ? contrastOfferViews(session, current.id, currentResult?.followUps) : [];
  const contrastAdded = contrastViews.filter((view) => view.status === "added").length;
  // Guided retry (pedagogy spec §5.3): the first try was withheld and the second is pending.
  const retryPending = currentResult?.retryPending === true;
  // Segmented parts (§4.3.4): a numeric item with several parts is checked one part at a time
  // until it is answered, unless the viewer chose to see every part at once.
  const segmentedMode =
    current !== undefined &&
    currentResult !== undefined &&
    isSegmentable(current) &&
    !showAllParts &&
    !currentResult.answered;
  const partChecks = currentResult?.partChecks;
  const partValueError = current ? (partValueErrorByQuestion[current.id] ?? false) : false;

  function setMcSelected(next: string[]) {
    if (!current) return;
    const id = current.id;
    setMcSelectedByQuestion((prev) => ({ ...prev, [id]: next }));
  }

  function setNumericValue(partId: string, value: string) {
    if (!current) return;
    const id = current.id;
    setNumericValuesByQuestion((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? {}), [partId]: value },
    }));
    // A refused part check complained about the old value; the next check reads the new one.
    setPartValueErrorByQuestion((prev) => (prev[id] ? { ...prev, [id]: false } : prev));
  }

  function setOpenText(next: string) {
    if (!current) return;
    const id = current.id;
    setOpenTextByQuestion((prev) => ({ ...prev, [id]: next }));
  }

  function setOpenTicked(next: string[]) {
    if (!current) return;
    const id = current.id;
    setOpenTickedByQuestion((prev) => ({ ...prev, [id]: next }));
    // A refused save's error is about the old ticks; the next save re-checks the new ones.
    setOpenMissedErrorByQuestion((prev) => ({ ...prev, [id]: false }));
  }

  function setOpenCorrection(next: string) {
    if (!current) return;
    const id = current.id;
    setOpenCorrectionByQuestion((prev) => ({ ...prev, [id]: next }));
  }

  function setOpenMissed(next: string) {
    if (!current) return;
    const id = current.id;
    setOpenMissedByQuestion((prev) => ({ ...prev, [id]: next }));
    setOpenMissedErrorByQuestion((prev) => ({ ...prev, [id]: false }));
  }

  function setOpenMissedChip(next: MissedChip) {
    if (!current) return;
    const id = current.id;
    setOpenMissedChipByQuestion((prev) => ({ ...prev, [id]: next }));
    setOpenMissedErrorByQuestion((prev) => ({ ...prev, [id]: false }));
  }

  function setJustification(next: string) {
    if (!current) return;
    const id = current.id;
    setJustificationByQuestion((prev) => ({ ...prev, [id]: next }));
    setJustificationErrorByQuestion((prev) => ({ ...prev, [id]: false }));
  }

  function setConfidence(level: ConfidenceLevel) {
    if (!current) return;
    const id = current.id;
    updateResult(id, { confidence: level });
    setConfidenceErrorByQuestion((prev) => ({ ...prev, [id]: false }));
  }

  /**
   * Pedagogy spec §13 Q1: a rating is required before "Svara"/"Klar". Returns
   * it, or shows the inline error, moves focus to the rating and returns
   * undefined. Inside the handlers, so the buttons and Enter/S share it.
   */
  function requireConfidence(): ConfidenceLevel | undefined {
    if (!current) return undefined;
    const level = currentResult?.confidence;
    if (level !== undefined) return level;
    const id = current.id;
    setConfidenceErrorByQuestion((prev) => ({ ...prev, [id]: true }));
    confidenceRef.current?.focus();
    return undefined;
  }

  /** One tap, final, no score effect (pedagogy spec §4.3.3). */
  function pickPlan(topicId: string) {
    if (!current || currentResult?.planPick !== undefined) return;
    updateResult(current.id, { planPick: topicId });
  }

  /**
   * True while the current question's plan step waits for its pick: the answer,
   * the hints and the give-up wait too, and focus moves to the plan group (§4.3.10).
   */
  function planWaits(): boolean {
    if (!planLocked) return false;
    planRef.current?.focus();
    return true;
  }

  // The summary's "Öva på liknande uppgifter" resolves its fetch and then calls this
  // in the same synchronous continuation as the `setSession` swap below — draft
  // answers are cleared in that same tick, so there is no async gap in which a draft
  // typed for the finished session could reappear under the new one's question ids.
  function restartSession(next: PracticeSessionData) {
    setMcSelectedByQuestion({});
    setNumericValuesByQuestion({});
    setOpenTextByQuestion({});
    setOpenTickedByQuestion({});
    setOpenCorrectionByQuestion({});
    setOpenMissedByQuestion({});
    setOpenMissedErrorByQuestion({});
    setJustificationByQuestion({});
    setJustificationErrorByQuestion({});
    setConfidenceErrorByQuestion({});
    setPartValueErrorByQuestion({});
    setSession(next);
  }

  useEffect(() => {
    if (session) saveSession(session);
  }, [session]);

  // The session this page is running, kept after unmount so a late answer finds its own session.
  const sessionIdRef = useRef(session?.id);
  sessionIdRef.current = session?.id;

  // A page that left while an answer was in flight patches the stored session when the answer
  // arrives (`updateResult`, `sendLeaveAnswer`). This page may have been mounted since (Back), with
  // the session loaded before the answer came back; its next save would overwrite the patch and
  // offer the same try again. It takes that one result over from storage instead.
  useEffect(() => {
    function onPatched(event: Event) {
      const { questionId, sessionId } = (event as CustomEvent<SessionPatchedDetail>).detail;
      const stored = loadSession();
      const patched = stored?.results[questionId];
      if (!stored || !patched) return;
      setSession((prev) => {
        if (!prev || prev.id !== sessionId || stored.id !== sessionId) return prev;
        if (!(questionId in prev.results)) return prev;
        return { ...prev, results: { ...prev.results, [questionId]: patched } };
      });
    }
    window.addEventListener(SESSION_PATCHED_EVENT, onPatched);
    return () => window.removeEventListener(SESSION_PATCHED_EVENT, onPatched);
  }, []);

  /**
   * Merges `patch` into a result. `clearConfidence` removes the rating: an optional field cannot
   * be patched back to undefined, and a segmented item rates each part afresh.
   */
  function updateResult(
    questionId: string,
    patch: Partial<QuestionResult>,
    clearConfidence = false,
  ) {
    if (!mountedRef.current) {
      // The page was left while the request ran: the answer is recorded, so the stored session
      // must say so, or coming back would offer the same try a second time.
      patchStoredResult(questionId, patch, {
        clearConfidence,
        ...(sessionIdRef.current !== undefined ? { sessionId: sessionIdRef.current } : {}),
      });
      return;
    }
    setSession((prev) => {
      if (!prev) return prev;
      const merged = { ...prev.results[questionId], ...patch } as QuestionResult;
      if (clearConfidence) delete merged.confidence;
      return { ...prev, results: { ...prev.results, [questionId]: merged } };
    });
  }

  /**
   * Writes a graded first try into the result: terminal, or pending when the server withheld the
   * solution and offered a retry. A pending item starts the retry fresh: the wrong parts and the
   * pick are cleared (the kept values come back from `firstTry.numericRaw`).
   */
  function applyFirstTry(
    questionId: string,
    existing: QuestionResult,
    res: AnswerResponse,
    sent: SentTry,
    extra: Partial<QuestionResult> = {},
  ) {
    if (isPending(res)) {
      updateResult(questionId, { ...pendingPatch(res, sent, existing), ...extra });
      setNumericValuesByQuestion((prev) => ({ ...prev, [questionId]: {} }));
      setMcSelectedByQuestion((prev) => ({ ...prev, [questionId]: [] }));
    } else {
      updateResult(questionId, { ...terminalPatch(res, sent), ...extra });
    }
  }

  /**
   * A request failed. A 404 means the stored question no longer exists (retired after a content
   * deploy, or never did): the set drops it and goes on instead of dead-ending on it (P0-1).
   */
  function reportSendError(error: unknown) {
    const gone = current?.id;
    if (!gone || !isCode(error, "NOT_FOUND")) {
      toast.error(messageForError(error, t));
      return;
    }
    toast.show(t("practice.session.questionGone"), "info");
    const remaining = session ? session.questions.filter((q) => q.id !== gone) : [];
    if (remaining.length === 0) {
      clearSession();
      setSession(null);
      return;
    }
    setSession((prev) => {
      if (!prev) return prev;
      const results = { ...prev.results };
      delete results[gone];
      return { ...prev, questions: prev.questions.filter((q) => q.id !== gone), results };
    });
  }

  async function submitMc() {
    if (!current || !currentResult || current.type !== "mc" || mcSelected.length === 0) return;
    const trimmed = justification.trim();
    // Decision #6, enforced here so the button and the Enter/S shortcuts share it.
    const missingJustification = current.requiresJustification === true && !trimmed;
    if (missingJustification) {
      const id = current.id;
      setJustificationErrorByQuestion((prev) => ({ ...prev, [id]: true }));
    }
    const confidence = requireConfidence();
    if (missingJustification) {
      // I1: a refused submit is otherwise silent to screen-reader and keyboard users —
      // move focus to the first field with an error (the justification sits above the
      // rating, so it wins over `requireConfidence`'s focus) so its message is read.
      justificationFieldRef.current?.focus();
      return;
    }
    if (confidence === undefined) return;
    try {
      const res = await answerMutation.mutateAsync({
        questionId: current.id,
        answer: { mc: mcSelected, ...(trimmed ? { justification: trimmed } : {}), hintsUsed },
        confidence,
        ...originBody,
        ...followUpFields,
      });
      applyFirstTry(current.id, currentResult, res, {
        mcSelected,
        ...(trimmed ? { justification: trimmed } : {}),
      });
    } catch (error) {
      reportSendError(error);
    }
  }

  async function submitNumeric() {
    if (!current || !currentResult || current.type !== "numeric") return;
    const confidence = requireConfidence();
    if (confidence === undefined) return;
    try {
      const res = await answerMutation.mutateAsync({
        questionId: current.id,
        answer: { numeric: numericValues, hintsUsed },
        confidence,
        ...planBody,
        ...originBody,
        ...followUpFields,
      });
      applyFirstTry(current.id, currentResult, res, { numericRaw: numericValues });
    } catch (error) {
      reportSendError(error);
    }
  }

  async function openKlar() {
    if (!current || current.type !== "open") return;
    // B6's guard lives in primarySubmit, but Ctrl+Enter in the textarea and AnswerArea's
    // Klar button both call this directly, so the same "one request at a time" check
    // belongs here too, not just at the primarySubmit entry point.
    if (answerMutation.isPending || revealMutation.isPending) return;
    if (requireConfidence() === undefined) return;
    try {
      // `openText` is the value from the render the click happened in: the pre-reveal text.
      const res = await revealMutation.mutateAsync({ questionId: current.id, giveUp: false });
      updateResult(current.id, {
        revealed: res.revealed,
        openRevealPending: true,
        preRevealText: openText,
      });
    } catch (error) {
      reportSendError(error);
    }
  }

  async function openGiveUp() {
    if (!current || current.type !== "open") return;
    // Same exposure as openKlar — its button leans on the same revealMutation.isPending
    // disabled state, so the same guard belongs here.
    if (answerMutation.isPending || revealMutation.isPending) return;
    if (planWaits()) return;
    try {
      // Pedagogy spec §4.3.3, §2.2: the give-up row keeps the pick, the hint count and the rating.
      const res = await revealMutation.mutateAsync({
        questionId: current.id,
        giveUp: true,
        ...planBody,
        hintsUsed,
        ...(currentResult?.confidence !== undefined
          ? { confidence: currentResult.confidence }
          : {}),
      });
      updateResult(current.id, { revealed: res.revealed, terminal: true, gaveUp: true });
    } catch (error) {
      reportSendError(error);
    }
  }

  async function openSave() {
    if (!current || current.type !== "open" || !currentResult) return;
    // P1-9: a quick answer satisfies the requirement; with own words it is sent as "chip – text".
    const missed = composeMissed(
      openMissedChip ? t(`practice.open.missedChip.${openMissedChip}`) : undefined,
      openMissed,
    );
    // Decision #5, enforced here so the button and the Enter/S shortcuts share it.
    if (
      currentResult.revealed?.type === "open" &&
      openBelowFullMarks(currentResult.revealed.rubric, openTicked, current.points) &&
      !missed
    ) {
      const id = current.id;
      setOpenMissedErrorByQuestion((prev) => ({ ...prev, [id]: true }));
      // I1: a refused save is otherwise silent to screen-reader and keyboard users —
      // move focus to the field with the error so its aria-describedby message is read.
      openMissedFieldRef.current?.focus();
      return;
    }
    // Cap defensively: a draft written before the textarea carried `maxLength` (or restored
    // from an older sessionStorage snapshot) may still exceed the server's limit. `capOpenText`
    // cuts to a UTF-16 length and never leaves a lone surrogate (bug 11). Sent only when it has
    // non-whitespace content — a whitespace-only draft is "nothing written" (bug 10), and the
    // text itself is sent untrimmed, as the student wrote it.
    const text = capOpenText(currentResult.preRevealText ?? openText);
    const correction = openCorrection.trim();
    try {
      const res = await answerMutation.mutateAsync({
        questionId: current.id,
        answer: {
          open: {
            rubricChecked: openTicked,
            ...(text.trim() ? { text } : {}),
            ...(correction ? { correction } : {}),
            // The chip label can push a full-length text past the server's cap.
            ...(missed ? { missed: capOpenText(missed) } : {}),
          },
          hintsUsed,
        },
        ...planBody,
        ...originBody,
        ...followUpFields,
        // Rated before "Klar"; sessions saved before the rating existed send none.
        ...(currentResult.confidence !== undefined ? { confidence: currentResult.confidence } : {}),
      });
      updateResult(current.id, {
        answered: true,
        terminal: true,
        correct: res.correct,
        score: res.score,
        maxScore: res.maxScore,
        ticked: res.ticked,
        // An open answer is never withheld; the solution shown at "Klar" stays if it ever were.
        ...(isPending(res) ? {} : { revealed: res.revealed }),
        openRevealPending: false,
        attemptId: res.attemptId,
        ...(isPending(res) ? {} : followUpPatch(res)),
      });
    } catch (error) {
      toast.error(messageForError(error, t));
    }
  }

  /** The retry gave up on the second try: the solution opens, nothing is graded. */
  async function skipRetry() {
    if (!current || !currentResult?.retryPending) return;
    // One request at a time, like every other action on this page.
    if (answerMutation.isPending || revealMutation.isPending) return;
    try {
      const res = await revealMutation.mutateAsync({ questionId: current.id });
      updateResult(current.id, skippedPatch(currentResult, res.revealed));
    } catch (error) {
      toast.error(messageForError(error, t));
    }
  }

  /**
   * The second try (pedagogy spec §5.3.6): the whole item again, with `retryOf` and the running
   * hint total. A retry the server refuses (400/404: too old, already retried, gone) shows a toast
   * and falls back to the solution, so the student is never stuck on a retry that cannot be saved.
   */
  async function submitRetry() {
    if (!current || !currentResult?.retryPending) return;
    if (answerMutation.isPending || revealMutation.isPending) return;
    if (current.type === "mc") {
      // A new pick is the retry; the first choice is shut.
      if (mcSelected.length === 0) return;
      if (current.requiresJustification === true && !justification.trim()) {
        const id = current.id;
        setJustificationErrorByQuestion((prev) => ({ ...prev, [id]: true }));
        justificationFieldRef.current?.focus();
        return;
      }
    }
    if (current.type === "numeric") {
      // A typo is fixed in place, not punished with a lost retry.
      const kept = new Set(keptPartIds(currentResult));
      const unreadable = current.parts.some((part) => {
        const raw = (numericValues[part.id] ?? "").trim();
        return !kept.has(part.id) && raw !== "" && "error" in parseNumericAnswer(raw);
      });
      if (unreadable) {
        toast.error(t("practice.retry.unreadable"));
        return;
      }
    }
    const body = retryBody(
      current,
      currentResult,
      { numeric: numericValues, mc: mcSelected, justification },
      hintsUsed,
    );
    if (!body) return;
    const trimmed = justification.trim();
    const sent: SentTry =
      current.type === "mc"
        ? { mcSelected, ...(trimmed ? { justification: trimmed } : {}) }
        : { numericRaw: body.answer.numeric ?? {} };
    try {
      const res = await answerMutation.mutateAsync(body);
      if (isPending(res)) {
        // The server never withholds a retry; if it ever did, the solution is the way out.
        if (mountedRef.current) await skipRetry();
        return;
      }
      updateResult(current.id, retriedPatch(res, sent, currentResult));
    } catch (error) {
      if (!mountedRef.current) return;
      if (error instanceof ApiError && (error.status === 400 || error.status === 404)) {
        toast.error(t("practice.retry.failed"));
        await skipRetry();
      } else {
        toast.error(messageForError(error, t));
      }
    }
  }

  function setPartValueError(questionId: string, on: boolean) {
    setPartValueErrorByQuestion((prev) => ({ ...prev, [questionId]: on }));
  }

  /**
   * Segmented items (§4.3.4): sends every checked part's first-try value, `segmented: true` and the
   * per-part ratings. Called by the last check, by "Rätta resten nu" (with the typed, rated part as
   * `draft`), and again by "Svara" when the automatic send failed.
   */
  async function submitSegmented(
    checks: Readonly<Record<string, PartCheck>>,
    draft?: SegmentedDraft,
  ) {
    if (!current || !currentResult || current.type !== "numeric") return;
    const body = segmentedBody({
      questionId: current.id,
      checks,
      ...(draft ? { draft } : {}),
      hintsUsed,
      ...planBody,
      ...originBody,
      followUp: followUpFields,
    });
    try {
      const res = await answerMutation.mutateAsync(body);
      applyFirstTry(
        current.id,
        currentResult,
        res,
        { numericRaw: body.answer.numeric ?? {} },
        {
          partChecks: checks,
          ...(body.confidence !== undefined ? { confidence: body.confidence } : {}),
        },
      );
    } catch (error) {
      reportSendError(error);
    }
  }

  /** The active part: checked on its own, locked, and the last one sends the answer. */
  async function checkActivePart() {
    if (!current || !currentResult || current.type !== "numeric") return;
    const part = activePart(current.parts, partChecks);
    if (!part) {
      // Every part is checked but the answer did not go out (a failed request): send it again.
      await submitSegmented(partChecks ?? {});
      return;
    }
    const value = (numericValues[part.id] ?? "").trim();
    if (value === "" || "error" in parseNumericAnswer(value)) {
      setPartValueError(current.id, true);
      segmentedRef.current?.focusActive();
      return;
    }
    setPartValueError(current.id, false);
    const confidence = requireConfidence();
    if (confidence === undefined) return;
    try {
      const res = await checkPartMutation.mutateAsync({
        questionId: current.id,
        partId: part.id,
        value,
      });
      const check: PartCheck = {
        value,
        confidence,
        outcome: res.outcome,
        ...(res.nearMiss ? { nearMissKind: res.nearMiss.kind } : {}),
      };
      const checks = { ...partChecks, [part.id]: check };
      // Left while the check ran: the leave already sent the item, with this part as its draft.
      if (!mountedRef.current) return;
      // The part locks; the next one starts unrated.
      updateResult(current.id, { partChecks: checks }, true);
      if (!activePart(current.parts, checks)) await submitSegmented(checks);
    } catch (error) {
      if (!mountedRef.current) return;
      reportSendError(error);
    }
  }

  /** "Rätta resten nu": sends what is checked, plus the active part if it was typed and rated. */
  async function checkRest() {
    if (!current || !currentResult || current.type !== "numeric") return;
    const part = activePart(current.parts, partChecks);
    const value = part ? (numericValues[part.id] ?? "").trim() : "";
    let draft: SegmentedDraft | undefined;
    if (part && value !== "") {
      if ("error" in parseNumericAnswer(value)) {
        setPartValueError(current.id, true);
        segmentedRef.current?.focusActive();
        return;
      }
      const confidence = requireConfidence();
      if (confidence === undefined) return;
      draft = { partId: part.id, value, confidence };
    }
    await submitSegmented(partChecks ?? {}, draft);
  }

  async function revealHint() {
    // The solution is already visible (Klar/give-up, or a graded MC/numeric answer) — a hint
    // opened now would only mark the attempt hint-assisted for nothing, so the `H` shortcut
    // stands down the same as the now-hidden button (T1, decision 2026-09-24).
    if (
      !current ||
      !currentResult ||
      currentResult.hintsRevealed.length >= current.hintCount ||
      currentResult.revealed
    )
      return;
    // The button disables while `hintMutation` is loading, but the H key doesn't — without
    // this, two fast presses both fetch the same index and append it twice.
    if (hintMutation.isPending) return;
    // A hint landing between a submit/give-up request and its response would show N+1 hints
    // while the stored attempt still says N — stand down while either is in flight.
    if (answerMutation.isPending || revealMutation.isPending) return;
    if (planWaits()) return;
    const questionId = current.id;
    const index = currentResult.hintsRevealed.length;
    try {
      const res = await hintMutation.mutateAsync({ questionId, index });
      // The student may have pressed Klar/Ge upp (or the answer may have graded) while this
      // request was in flight — read the *latest* result via the functional update rather than
      // the closed-over `currentResult`, and drop the hint if it landed after the reveal, so a
      // hint that arrives too late to matter doesn't inflate `hintsUsed` post-reveal.
      setSession((prev) => {
        if (!prev) return prev;
        const existing = prev.results[questionId];
        // Also stale if another hint request already appended at this index (or shifted past
        // it) while this one was in flight — appending here would duplicate the hint.
        if (!existing || existing.revealed || existing.hintsRevealed.length !== index) return prev;
        return {
          ...prev,
          results: {
            ...prev.results,
            [questionId]: { ...existing, hintsRevealed: [...existing.hintsRevealed, res.hint] },
          },
        };
      });
    } catch (error) {
      reportSendError(error);
    }
  }

  function next() {
    setSession((prev) => (prev ? { ...prev, index: prev.index + 1 } : prev));
  }

  /** Decision #10: a fresh related problem, inserted right after the current one. */
  async function practiseRelated() {
    if (!session || !current) return;
    const id = current.id;
    try {
      const res = await relatedMutation.mutateAsync({
        questionIds: [id],
        exclude: session.questions.map((question) => question.id),
      });
      const found = res.related[0]?.question ?? null;
      // Splice after the question this fetch was for, by id: by the time the fetch resolves,
      // `index` may already have advanced (bug 1, C18).
      focusNextAfterRelated.current = true;
      setSession((prev) => {
        if (!prev) return prev;
        const inserted = found ? insertAfter(prev, id, found, similarState(id)) : null;
        const base = inserted ?? prev;
        const relatedStatus: QuestionResult["relatedStatus"] = inserted ? "added" : "none";
        return {
          ...base,
          results: { ...base.results, [id]: { ...base.results[id], relatedStatus } },
        };
      });
    } catch (error) {
      toast.error(messageForError(error, t));
      // B5: the button is disabled while the request is pending, so focus falls off it
      // to <body> and stays there behind the error toast — move it to a sensible
      // control instead of leaving a keyboard user stranded.
      nextButtonRef.current?.focus();
    }
  }

  /**
   * C-6: the contrast twin arrived with the offer, so inserting it is synchronous. The prediction
   * was required by the card; the twin lands right after this question and carries the choice.
   */
  function startContrast(offer: ContrastOffer, choice: string | undefined) {
    if (!current) return;
    const id = current.id;
    focusNextAfterRelated.current = true;
    setSession((prev) => (prev ? (insertContrast(prev, id, offer, choice) ?? prev) : prev));
  }

  function primarySubmit() {
    if (!current || !currentResult || currentResult.terminal) return;
    // B6: without this guard, S/Enter pressed twice during a slow request (the button
    // is disabled but a window-level keydown still fires) posted the attempt twice.
    if (answerMutation.isPending || revealMutation.isPending || checkPartMutation.isPending) return;
    if (planWaits()) return;
    // Enter and S are "Kontrollera igen" while a retry is pending (spec §5.3.6).
    if (currentResult.retryPending) void submitRetry();
    else if (current.type === "mc") void submitMc();
    else if (current.type === "numeric") {
      if (segmentedMode) void checkActivePart();
      else void submitNumeric();
    } else if (currentResult.openRevealPending) void openSave();
    else void openKlar();
  }

  function onCheckRest() {
    if (answerMutation.isPending || checkPartMutation.isPending) return;
    if (planWaits()) return;
    void checkRest();
  }

  // I1: focused when a submit/save is refused, so the inline error is read out and the
  // student can type right away instead of the error being silent (WCAG 3.3.1 / 4.1.3).
  const justificationFieldRef = useRef<HTMLTextAreaElement>(null);
  const openMissedFieldRef = useRef<HTMLInputElement>(null);

  const nextButtonRef = useRef<HTMLButtonElement>(null);
  // Set right before the related-question fetch starts, cleared by the effect below once it
  // has moved focus — so only a change that came from this click steals focus (bug 14, C18).
  const focusNextAfterRelated = useRef(false);
  useEffect(() => {
    if (!focusNextAfterRelated.current) return;
    focusNextAfterRelated.current = false;
    nextButtonRef.current?.focus();
  }, [currentResult?.relatedStatus, contrastAdded]);

  // Refreshed every render so the (subscribe-once) keydown listener always
  // calls the latest closures without re-attaching the listener each keystroke.
  const latest = useRef({
    primarySubmit,
    next,
    revealHint,
    terminal: currentResult?.terminal ?? false,
    relatedPending: relatedMutation.isPending,
  });
  latest.current = {
    primarySubmit,
    next,
    revealHint,
    terminal: currentResult?.terminal ?? false,
    relatedPending: relatedMutation.isPending,
  };

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (shortcutsBlocked(event)) return;
      if (event.repeat) return;
      if (event.key === "Enter" && !latest.current.terminal) {
        event.preventDefault();
        latest.current.primarySubmit();
      } else if (
        (event.key === "n" || event.key === "N") &&
        latest.current.terminal &&
        !latest.current.relatedPending
      ) {
        latest.current.next();
      } else if (event.key === "h" || event.key === "H") {
        latest.current.revealHint();
      } else if (event.key === "s" || event.key === "S") {
        // N2: a refused submit/save moves focus into the now-empty required field in
        // the same keydown dispatch (I1, in submitMc/openSave) — without this, the
        // browser's default action for "s" still ran against that newly-focused field
        // afterwards and typed a stray "s" into it, same as Enter already prevents.
        event.preventDefault();
        latest.current.primarySubmit();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Leaving a half-checked segmented item sends what was checked (pedagogy spec §4.3.4).
  // A part whose check is still in flight counts as checked: the student committed it, and its
  // late answer is dropped on the unmounted page, so the leave carries it as the draft.
  const checking = checkPartMutation.isPending ? checkPartMutation.variables : undefined;
  const leaveDraft: SegmentedDraft | undefined =
    checking && checking.questionId === current?.id && currentResult?.confidence !== undefined
      ? { partId: checking.partId, value: checking.value, confidence: currentResult.confidence }
      : undefined;
  const leaveBody: AnswerBody | null =
    segmentedMode &&
    current &&
    partChecks &&
    Object.keys(partChecks).length > 0 &&
    !answerMutation.isPending
      ? segmentedBody({
          questionId: current.id,
          checks: partChecks,
          ...(leaveDraft ? { draft: leaveDraft } : {}),
          hintsUsed,
          ...planBody,
          ...originBody,
          followUp: followUpFields,
          offerRetry: false,
        })
      : null;
  const leaveRef = useRef(leaveBody);
  leaveRef.current = leaveBody;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const body = leaveRef.current;
      if (!body) return;
      const sessionId = sessionIdRef.current;
      // StrictMode unmounts and remounts at once in development: only a real leave sends. The
      // recorded try belongs in "Senaste övningar", which caches the history for a while.
      setTimeout(() => {
        if (mountedRef.current) return;
        void sendLeaveAnswer(body, sessionId).then(() =>
          queryClient.invalidateQueries({ queryKey: practiceKeys.all }),
        );
      }, 0);
    };
  }, [queryClient]);

  if (!session) return <Navigate to="/practice" replace />;

  const total = session.questions.length;
  if (session.index >= total) {
    return (
      <div className="mx-auto max-w-2xl">
        <SessionSummary session={session} onRestart={restartSession} />
      </div>
    );
  }
  if (!current || !currentResult) return null;

  // "Öva på en liknande uppgift" is the fallback when no contrast twin is on offer (§5.3.8), and
  // a follow-up offers no follow-ups (depth 1).
  const offerRelated =
    currentResult.answered &&
    !currentResult.gaveUp &&
    (currentResult.score ?? 0) < (currentResult.maxScore ?? 0) &&
    currentResult.followUp === undefined &&
    contrastViews.length === 0;
  const relatedStatusText =
    currentResult.relatedStatus === "added"
      ? t("practice.feedback.relatedAdded")
      : currentResult.relatedStatus === "none"
        ? t("practice.feedback.noRelated")
        : "";

  const firstTry = currentResult.firstTry;
  const retryProps =
    retryPending && firstTry
      ? {
          keptPartIds: keptPartIds(currentResult),
          keptValues: firstTry.numericRaw ?? {},
          ...(firstTry.mcSelected ? { firstChoice: firstTry.mcSelected } : {}),
        }
      : undefined;

  // Segmented parts (B4): the rows, the per-part rating and the latest check, announced politely.
  const lastChecked =
    current.type === "numeric"
      ? [...current.parts].reverse().find((part) => partChecks?.[part.id] !== undefined)
      : undefined;
  const lastOutcome = lastChecked ? partChecks?.[lastChecked.id]?.outcome : undefined;
  const segmentedProps =
    segmentedMode && current.type === "numeric"
      ? {
          checks: partChecks ?? {},
          values: numericValues,
          onValueChange: setNumericValue,
          confidence: currentResult.confidence,
          onConfidenceChange: setConfidence,
          confidenceError: confidenceError ? t("practice.confidence.required") : undefined,
          confidenceRef,
          valueError: partValueError ? t("practice.part.valueRequired") : undefined,
          announcement:
            lastChecked && lastOutcome
              ? t(lastOutcome === "right" ? "practice.part.right" : "practice.part.wrong", {
                  part: lastChecked.id,
                })
              : "",
          onPrimary: primarySubmit,
          onCheckRest,
          onRevealHint: () => void revealHint(),
          checking: checkPartMutation.isPending,
          submitting: answerMutation.isPending,
          activeRef: segmentedRef,
        }
      : undefined;
  const showAllProps =
    current.type === "numeric" && current.parts.length > 1
      ? {
          checked: showAllParts,
          onChange: (on: boolean) => {
            setShowAllParts(on);
            saveShowAllParts(on);
          },
          // Switching mid-item would strand the checked parts.
          disabled: Object.keys(partChecks ?? {}).length > 0,
        }
      : undefined;

  const ladder = (
    <HintLadder
      question={current}
      revealed={currentResult.hintsRevealed}
      onRevealNext={() => void revealHint()}
      loading={hintMutation.isPending}
      locked={planLocked}
      solutionShown={Boolean(currentResult.revealed)}
    />
  );
  const answerArea = (
    <AnswerArea
      question={current}
      result={currentResult}
      mcSelected={mcSelected}
      onMcChange={setMcSelected}
      numericValues={numericValues}
      onNumericChange={setNumericValue}
      openText={openText}
      onOpenTextChange={setOpenText}
      openTicked={openTicked}
      onOpenTickedChange={setOpenTicked}
      onSubmit={primarySubmit}
      onOpenKlar={() => void openKlar()}
      onOpenGiveUp={() => void openGiveUp()}
      onOpenSave={() => void openSave()}
      openCorrection={openCorrection}
      onOpenCorrectionChange={setOpenCorrection}
      openMissed={openMissed}
      onOpenMissedChange={setOpenMissed}
      openMissedChip={openMissedChip}
      onOpenMissedChipChange={setOpenMissedChip}
      openMissedError={openMissedError ? t("practice.open.missedRequired") : undefined}
      justification={justification}
      onJustificationChange={setJustification}
      openMissedFieldRef={openMissedFieldRef}
      justificationError={justificationError ? t("practice.mc.justificationMissing") : undefined}
      justificationFieldRef={justificationFieldRef}
      onConfidenceChange={setConfidence}
      confidenceError={confidenceError ? t("practice.confidence.required") : undefined}
      confidenceRef={confidenceRef}
      onRevealHint={() => void revealHint()}
      submitting={answerMutation.isPending}
      revealing={revealMutation.isPending}
      retry={retryProps}
      segmented={segmentedProps}
      showAll={showAllProps}
    />
  );

  return (
    <div className="mx-auto max-w-2xl lg:max-w-6xl">
      <h1
        ref={h1Ref}
        tabIndex={-1}
        className={
          session.lecture ? "mb-4 font-display text-xl outline-none" : "sr-only outline-none"
        }
      >
        {session.lecture && `${session.lecture.id} · ${localize(session.lecture.title)} · `}
        {t("practice.session.heading", { current: session.index + 1, total })}
      </h1>
      {session.timed && <TimedHeader timed={session.timed} />}
      <ProgressBar
        value={session.index / total}
        label={t("practice.session.progress")}
        caption={`${session.index + 1}/${total}`}
        className="mb-4"
      />
      <div className="lg:grid lg:grid-cols-[minmax(0,11fr)_minmax(0,9fr)] lg:gap-6">
        <section aria-labelledby={problemHeadingId}>
          <h2 id={problemHeadingId} className="sr-only">
            {t("practice.session.problemRegion")}
          </h2>
          {/* Sticky below the app header (`top-20`), capped at the visible height. */}
          <div
            ref={problemRef}
            className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto"
          >
            {/* P1-4: the chip shows in a topic or lecture set. A lecture set (LectureSetup) is
                stored as mode "topic" plus `lecture`; both are tested so a future mode keeps it. */}
            <QuestionCard
              question={current}
              showTopic={session.mode === "topic" || session.lecture !== undefined}
            />
          </div>
        </section>
        <section aria-labelledby={workHeadingId}>
          <h2 id={workHeadingId} className="sr-only">
            {t("practice.session.workRegion")}
          </h2>
          <FollowUpBanner followUp={currentResult.followUp} />
          {planOffer && (
            <PlanStep
              ref={planRef}
              offer={planOffer}
              pick={currentResult.planPick}
              onPick={pickPlan}
            />
          )}
          {retryPending && currentResult.firstTry ? (
            // The withheld first try: diagnosis, ladder, the inputs of the wrong parts, and the
            // two ways on. Enter and S are "Kontrollera igen"; N waits for the terminal state.
            <RetryPanel
              kind={current.type === "mc" ? "mc" : "numeric"}
              parts={currentResult.firstTry.parts ?? []}
              numericRaw={currentResult.firstTry.numericRaw}
              confidenceOf={(partId) =>
                partChecks?.[partId]?.confidence ?? currentResult.confidence
              }
              mcDiagnosis={currentResult.firstTry.mcDiagnosis}
              autoHint={currentResult.firstTry.autoHint === true}
              hintLadder={ladder}
              onSubmit={primarySubmit}
              onStuck={() => void skipRetry()}
              selfExplanation={
                <SelfExplanation attemptId={currentResult.firstTry.attemptId} correct={false} />
              }
              submitting={answerMutation.isPending}
              revealing={revealMutation.isPending}
              submitDisabled={current.type === "mc" && mcSelected.length === 0}
            >
              {answerArea}
            </RetryPanel>
          ) : (
            <>
              {ladder}
              <PlanGate active={planOffer !== undefined} locked={planLocked}>
                {answerArea}
              </PlanGate>
            </>
          )}
          <FormulaPanel
            key={current.id}
            topic={current.topic}
            secondaryTopics={current.secondaryTopics}
            locked={planLocked}
          />
          {currentResult.terminal && (
            <>
              {/* `QuestionResult.attemptId` is the latest stored row (the retry's after a retry,
                  else the first try's): the sentence is saved on it. */}
              <FeedbackPanel
                key={current.id}
                result={currentResult}
                question={current}
                selfExplanation={<ResultSelfExplanation result={currentResult} />}
              />
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                <NextSteps
                  retestDue={currentResult.retestDue}
                  contrasts={contrastViews}
                  onStartContrast={startContrast}
                />
                {offerRelated && (
                  <p role="status" className="mr-auto text-sm text-ink-600 dark:text-ink-400">
                    {relatedStatusText}
                  </p>
                )}
                {offerRelated && !currentResult.relatedStatus && (
                  <Button
                    variant="secondary"
                    onClick={() => void practiseRelated()}
                    loading={relatedMutation.isPending}
                  >
                    {t("practice.feedback.tryRelated")}
                  </Button>
                )}
                <Button
                  ref={nextButtonRef}
                  onClick={next}
                  disabled={relatedMutation.isPending}
                  iconRight={<ArrowRight size={16} aria-hidden="true" />}
                >
                  {t("common.next")} <Kbd className="ml-1">N</Kbd>
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default PracticeSessionPage;
